import { useEffect, useState } from 'react';
/**
 * @fileoverview Custom React hook for detecting online/offline status
 * @module hooks/useOnlineStatus
 * 
 * This hook monitors the browser's network connection status and returns
 * a boolean indicating whether the user is currently online.
 * 
 * Useful for:
 * - Showing offline banners
 * - Disabling features that require internet
 * - Queuing requests when offline
 * - Providing better UX during network issues
 */

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};


/**
 * Hook to detect if the user is currently online or offline
 * 
 * @returns {boolean} true if online, false if offline
 * 
 * @example
 * function MyComponent() {
 *   const isOnline = useOnlineStatus();
 *   
 *   return (
 *     <div>
 *       {!isOnline && (
 *         <div className="offline-banner">
 *           You're offline. Some features may not work.
 *         </div>
 *       )}
 *     </div>
 *   );
 * }
 * 
 * @features
 * - Listens to browser 'online' and 'offline' events
 * - Updates state automatically when connection changes
 * - Cleans up event listeners on unmount
 * - Initial state from navigator.onLine
 * 
 * @browser-support
 * - Chrome: ✅ Full support
 * - Firefox: ✅ Full support
 * - Safari: ✅ Full support
 * - Edge: ✅ Full support
 * - IE11: ⚠️ Partial support (may not detect all offline scenarios)
 */